import _ from 'lodash';
import moment from 'moment';
import Highcharts from 'highcharts';
import { CHART_COLOR, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { logError } from '@/shared/lib/apiHelpers';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  isValidHr,
  filterHourlySummaryRanges,
  createMinutelyHrChartData,
  createChartData,
} from './heartRateCharts';
import { getMinMaxChart, validateEcgDataMap } from './chartRange';

export const ZOOM_SCALE_DEFAULT = [100, 200, 400, 500, 1000, 2000]; // 0,2,4,5,10,20
export const DAY_EPOCH = 86340000;

// when element has value Y != null, this element will push into array result
/**
 * Overlays up to four heart-rate chart series into one, preferring the highest-priority series that has a value at each minute. Walks the first non-empty series for its length and takes the first of the first three arrays whose y is not null at that index, falling back to the fourth array and then to the reference series.
 *
 * @param arr1 - Highest priority series.
 * @param arr2 - Second priority series.
 * @param arr3 - Third priority series.
 * @param arr4 - Last resort series, taken without a null check.
 * @returns The merged point array, empty when every series is missing.
 */
export const mergeDataChart = (arr1: any[], arr2: any[], arr3: any[], arr4: any[]) => {
  if (!arr1 && !arr2 && !arr3 && !arr4) {
    return [];
  }
  const arrMax = arr1?.length > 0 ? arr1 : arr2?.length > 0 ? arr2 : arr3?.length > 0 ? arr3 : arr4;
  const arr = [];
  for (let i = 0; i < arrMax.length; i += 1) {
    if (arr1 && arr1[i].y !== null) {
      arr.push(arr1[i]);
    } else if (arr2 && arr2[i].y !== null) {
      arr.push(arr2[i]);
    } else if (arr3 && arr3[i].y !== null) {
      arr.push(arr3[i]);
    } else if (arr4) {
      arr.push(arr4[i]);
    } else {
      arr.push(arrMax[i]);
    }
  }
  return arr;
};

// If there are multiple values at a time,
// prioritize the values with the color "url" and then "!CHART_COLOR.WHITE".
/**
 * Collapses chart zone stops sitting at the same value down to a single stop. Groups by value and, where a group holds more than one stop, prefers one whose colour is a pattern url, then the first non-white colour, and finally the first entry.
 *
 * @param arrSort - Zone stops sorted by value, possibly with duplicates.
 * @returns One zone stop per distinct value.
 */
const removeSameValue = (arrSort: any[]) => {
  const groupedData = _.groupBy(arrSort, 'value');
  const result = _.map(groupedData, (d) => {
    if (d.length === 1) {
      return d[0];
    }
    let ele = _.find(d, (x) => _.includes(x.color, 'url'));
    if (ele) {
      return ele;
    }
    ele = _.find(d, (x) => !_.includes(x.color, CHART_COLOR.WHITE));
    return ele || d[0];
  });
  return result;
};

// merge all array into a array, then sort by time
/**
 * Flattens several chart zone lists into one sorted zone list. Concatenates each list, sorts by value and collapses duplicate values, preferring pattern url and non-white colours.
 *
 * @param listArray - The zone lists to combine.
 * @returns The merged zone stops sorted by value.
 */
export const mergeZonesChart = (listArray: any[]) => {
  let arr: any[] = [];
  for (let i = 0; i < listArray.length; i += 1) {
    arr = arr.concat(listArray[i]);
  }

  const arrSort = arr.sort((a, b) => a.value - b.value);

  // remove element same as value
  return removeSameValue(arrSort);
};

/**
 * Resolves the colour an inserted zone start should take from the zone stops around it. When a stop already sits at the same value it reuses the winner of the pattern-then-non-white preference, and otherwise inserts the start into a value-sorted copy and takes the colour of the following stop, or its own when it lands last.
 *
 * @param arr - The existing zone stops.
 * @param start - The zone start being inserted, read for its value.
 * @returns The colour string the zone start should carry.
 */
const getColorStart = (arr: any[], start: any) => {
  const arrClone = _.cloneDeep(arr);
  // find point same value
  const ele = arrClone.find((x) => x.value === start.value);
  if (ele) {
    const arrMerge = removeSameValue([start, ele]);
    return arrMerge[0].color;
  }
  // don't find value
  arrClone.push(start);
  const arrSort = arrClone.sort((a, b) => a.value - b.value);
  const index = _.findIndex(arrSort, (x) => x.value === start.value);
  if (index > -1 && index < arrSort.length - 1) {
    return arrSort[index + 1].color;
  }
  return arrSort[index].color;
};

// this function return start, stop and update Arr.
/**
 * Recolours the zone stops an overlapping event spans on the heart-rate chart. Gives the start the colour of the zones around it, then in a value-sorted deduplicated copy walks every stop strictly between the start and stop and repaints it with the stop's colour unless it already carries a pattern url.
 *
 * @param arr - The base zone stops, repainted in place.
 * @param start - The overlap's leading zone stop, whose colour is resolved here.
 * @param stop - The overlap's trailing zone stop, whose colour is spread across the span.
 * @returns An object carrying the recoloured start and stop zone points.
 */
const updateColorPoint = (arr: any[], start: any, stop: any) => {
  // update color start
  start.color = getColorStart(arr, start);

  // update color stop
  const arrClone = _.cloneDeep(arr);
  arrClone.push(start);
  arrClone.push(stop);
  const arrSortT = arrClone.sort((a, b) => a.value - b.value);
  const arrSort = removeSameValue(arrSortT);
  const indexStart = _.findIndex(arrSort, (x) => x.value === start.value);
  const indexStop = _.findIndex(arrSort, (x) => x.value === stop.value);

  const durationIndex = indexStop - indexStart;
  // Khi event overlap len event khac
  if (durationIndex > 1) {
    for (let i = indexStart + 1; i < indexStop; i += 1) {
      const ele = arr.find((x) => x.value === arrSort[i].value);
      if (ele) {
        // uu tien zone da thay doi
        ele.color = ele.color.includes('url') ? ele.color : stop.color;
      }
    }
  }
  return { start, stop };
};

// handle other event overlap. arrD overlap on arrS
/**
 * Merges a second event's zone list onto an existing heart-rate chart zone list, keeping overlapping colours consistent. Synthesises a white zone at the start of the day when the overlay opens on a coloured point, repaints the base stops spanned by each white to coloured transition, then concatenates both lists and collapses duplicate values.
 *
 * @param arrS - The base zone stops, cloned before use.
 * @param arrD - The overlay zone stops, cloned and walked for transitions.
 * @returns The combined zone stops sorted by value.
 */
export const handleZoneOtherEventOverlap = (arrS: any[], arrD: any[]) => {
  const arr = _.cloneDeep(arrS);
  const arr1 = _.cloneDeep(arrD);
  // don't handle last point
  for (let i = 0; i < arr1.length - 1; i += 1) {
    if (i === 0 && arr1[0].color !== CHART_COLOR.WHITE) {
      const startEvent = {
        value: moment(arr1[0].value).startOf('days').valueOf(),
        color: CHART_COLOR.WHITE,
        time: '00:00',
      };
      updateColorPoint(arr, startEvent, arr1[0]);
    } else if (arr1[i].color === CHART_COLOR.WHITE && arr1[i + 1].color !== CHART_COLOR.WHITE) {
      const { start, stop } = updateColorPoint(arr, arr1[i], arr1[i + 1]);
      arr1[i] = start;
      arr1[i + 1] = stop;
    }
  }
  return mergeZonesChart([arr, arr1]);
};

// null -> maxHR
/**
 * Turns a sparse heart-rate chart series into coloured zone bands covering its runs of consecutive data. Walks the points tracking the current run's start, length and last non-null point, and each time a run of more than one point ends, or the series itself ends, pushes a white stop at the run start and a coloured stop at its end, both labelled with the local hour and minute.
 *
 * @param arr - The chart points, whose null y values delimit the runs.
 * @param color - Colour applied to the stop that closes each run.
 * @param timezoneOffset - Offset applied when formatting each stop's time label.
 * @returns An array of zone stops delimiting each run of data.
 */
export const createZoneFromDataChart = (arr: any[], color: any, timezoneOffset: number) => {
  const zones: any[] = [];
  let item: any = {
    start: null,
    stop: null,
    count: 0,
    oldPoint: { y: null },
  };
  if (arr[0]?.y) {
    item = {
      start: arr[0].x,
      stop: null,
      count: 1,
      oldPoint: arr[0],
    };
  }
  const arrLength = arr.length;
  for (let i = 1; i < arrLength; i += 1) {
    // end array
    if (i === arrLength - 1) {
      if (arr[i].y) {
        _.assign(item, {
          stop: arr[i].x,
          count: item.count + 1,
          oldPoint: arr[i],
        });
      } else {
        _.assign(item, {
          stop: item.oldPoint.x,
        });
      }
      // push 1 event
      if (item.count > 1 && item.start) {
        zones.push({
          value: item.start,
          color: CHART_COLOR.WHITE,
          time: convertToMomentJs(item.start, timezoneOffset).format('HH:mm'),
        });
        zones.push({
          value: item.stop,
          color,
          time: convertToMomentJs(item?.stop, timezoneOffset).format('HH:mm'),
        });
      }
    }

    // change y value (max -> null or null -> max)
    if (item.oldPoint?.y !== arr[i].y) {
      // start 1 event
      if (arr[i].y) {
        _.assign(item, {
          start: arr[i].x,
          count: item.count + 1,
          oldPoint: arr[i],
        });
      } else {
        // end 1 event
        _.assign(item, {
          stop: item.oldPoint.x,
        });

        // add event into array
        if (item.count > 1 && item.start) {
          zones.push({
            value: item.start,
            color: CHART_COLOR.WHITE,
            time: convertToMomentJs(item.start, timezoneOffset).format('HH:mm'),
          });
          zones.push({
            value: item.stop,
            color,
            time: convertToMomentJs(item?.stop, timezoneOffset).format('HH:mm'),
          });
        }

        // reset item
        item = {
          start: null,
          stop: null,
          count: 0,
          oldPoint: { y: null },
        };
      }
    } else if (arr[i].y) {
      _.assign(item, {
        count: item.count + 1,
        oldPoint: arr[i],
      });
    }
  }
  return zones;
};

/**
 * Strips the gap points out of a heart-rate chart series. Keeps only the points whose y is not null.
 *
 * @param arr - The chart points to filter.
 * @returns A new array without the null-valued points.
 */
const removePointYNull = (arr: any[]) => {
  const result = arr.filter((x) => x.y !== null);
  return result;
};

export const generateZoneChart = ({
  artifactEventAreaPointsData,
  afibEventAreaPointsData,
  otherEventAreaPointsData,
  timezoneOffset = 0,
}: {
  artifactEventAreaPointsData: any[];
  afibEventAreaPointsData: any[];
  otherEventAreaPointsData: any[];
  timezoneOffset?: number;
}) => {
  // get zone
  const zoneArtifact = createZoneFromDataChart(
    artifactEventAreaPointsData,
    CHART_COLOR.STEEL_GRAY_LIGHT,
    timezoneOffset,
  );
  const zoneAfib = createZoneFromDataChart(
    afibEventAreaPointsData,
    CHART_COLOR.AMBER_YELLOW_LIGHT,
    timezoneOffset,
  );
  const zoneOther = createZoneFromDataChart(
    otherEventAreaPointsData,
    CHART_COLOR.CRIMSON_RED_LIGHT,
    timezoneOffset,
  );
  return {
    zoneArtifact,
    zoneAfib,
    zoneOther,
  };
};

export const generateDataHRChart = ({
  missingPointsData,
  artifactEventAreaPointsData,
  afibEventAreaPointsData,
  otherEventAreaPointsData,
  hrsData,
  zoneArtifact,
  zoneAfib,
  zoneOther,
  missingPointsZone,
  negativeHrsZones,
  unsaveArtifactEventsZones,
  unsaveAfibEventsZones,
  unsaveOtherEventsZones,
  artifactEventGuidesData,
  afibEventGuidesData,
  otherEventGuidesData,
  isShowArtifactEvent,
  isShowAfibEvent,
  isShowOtherEvent,
  activeButton,
}: {
  missingPointsData: any[];
  artifactEventAreaPointsData: any[];
  afibEventAreaPointsData: any[];
  otherEventAreaPointsData: any[];
  hrsData: any;
  zoneArtifact: any[];
  zoneAfib: any[];
  zoneOther: any[];
  missingPointsZone: any[];
  negativeHrsZones: any[];
  unsaveArtifactEventsZones: any[];
  unsaveAfibEventsZones: any[];
  unsaveOtherEventsZones: any[];
  artifactEventGuidesData: any[];
  afibEventGuidesData: any[];
  otherEventGuidesData: any[];
  isShowArtifactEvent: boolean;
  isShowAfibEvent: boolean;
  isShowOtherEvent: boolean;
  activeButton: any;
}) => {
  // data for area
  const dataAreaChart = mergeDataChart(
    missingPointsData,
    isShowArtifactEvent ? artifactEventAreaPointsData : null,
    isShowAfibEvent ? afibEventAreaPointsData : null,
    isShowOtherEvent ? otherEventAreaPointsData : null,
  );
  let dataAreaChartZone = mergeZonesChart([
    missingPointsZone,
    isShowArtifactEvent ? unsaveArtifactEventsZones : [],
    isShowAfibEvent ? unsaveAfibEventsZones : [],
    isShowOtherEvent ? unsaveOtherEventsZones : [],
  ]);
  if (isShowArtifactEvent) {
    dataAreaChartZone = handleZoneOtherEventOverlap(dataAreaChartZone, zoneArtifact);
  }
  if (isShowAfibEvent) {
    dataAreaChartZone = handleZoneOtherEventOverlap(dataAreaChartZone, zoneAfib);
  }
  if (isShowOtherEvent) {
    dataAreaChartZone = handleZoneOtherEventOverlap(dataAreaChartZone, zoneOther);
  }

  // *: Make guides be displayed with priority
  const dataChartArray: any[] = [];
  if (!_.includes([TYPE_RHYTHM_EVENT_ENUM.ARTIFACT, TYPE_RHYTHM_EVENT_ENUM.AFIB], activeButton)) {
    dataChartArray.push(
      isShowOtherEvent ? otherEventGuidesData : null,
      isShowAfibEvent ? afibEventGuidesData : null,
      isShowArtifactEvent ? artifactEventGuidesData : null,
    );
  } else if (activeButton === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
    dataChartArray.push(
      isShowAfibEvent ? afibEventGuidesData : null,
      isShowOtherEvent ? otherEventGuidesData : null,
      isShowArtifactEvent ? artifactEventGuidesData : null,
    );
  } else {
    dataChartArray.push(
      isShowArtifactEvent ? artifactEventGuidesData : null,
      isShowAfibEvent ? afibEventGuidesData : null,
      isShowOtherEvent ? otherEventGuidesData : null,
    );
  }

  // data for columns, (apply when duration data only 1min)
  const dataColumnChart = mergeDataChart(...(dataChartArray as [any, any, any, any]));
  return {
    dataAreaChart: removePointYNull(dataAreaChart),
    dataAreaChartZone,
    dataColumnChart: _.cloneDeep(dataColumnChart),
    dataHrsData: hrsData,
    dataNegativeHrsZones: negativeHrsZones,
  };
};

export const configChartHR = ({
  dataAreaChart,
  dataAreaChartZone,
  dataColumnChart,
  dataHrsData,
  dataNegativeHrsZones,
  minHr,
  maxHr,
  timezoneOffset = 0,
  eventLoad,
  eventClick,
  eventAfterSetExtremes,
  idHtmlChart,
  firstEpoch,
  isOldUI = false,
}: {
  dataAreaChart: any[];
  dataAreaChartZone: any[];
  dataColumnChart: any[];
  dataHrsData: any[];
  dataNegativeHrsZones: any[];
  minHr: number;
  maxHr: number;
  timezoneOffset?: number;
  eventLoad: any;
  eventClick: any;
  eventAfterSetExtremes: any;
  idHtmlChart: any;
  firstEpoch: number;
  isOldUI?: boolean;
}) => {
  const idMissingDataPattern = `missing-data-custom-pattern-${idHtmlChart}`;
  const idMissingDataWithAfibPattern = `missing-data-with-afib-custom-pattern-${idHtmlChart}`;
  const idMissingDataWithUnsaveAfibPattern = `missing-data-with-unsave-afib-custom-pattern-${idHtmlChart}`;
  const idUnsaveArtifactPattern = `unsave-artifact-event-custom-pattern-${idHtmlChart}`;
  const idUnsaveAfibPattern = `unsave-afib-event-custom-pattern-${idHtmlChart}`;
  const idUnsaveOtherPattern = `unsave-other-event-custom-pattern-${idHtmlChart}`;
  const idSelectedUnsaveArtifactPattern = `selected-unsave-artifact-event-custom-pattern-${idHtmlChart}`;
  const idSelectedUnsaveAfibPattern = `selected-unsave-afib-event-custom-pattern-${idHtmlChart}`;
  const idSelectedUnsaveOtherPattern = `selected-unsave-other-event-custom-pattern-${idHtmlChart}`;
  const idSelectedMissingDataWithUnsaveAfibPattern = `selected-missing-data-with-unsave-afib-custom-pattern-${idHtmlChart}`;

  const config = {
    defs: {
      patterns: [
        {
          id: idMissingDataPattern,
          path: {
            d: 'M0.207151 6H0V4.79294L4.79294 0H6V0.207151L0.207151 6Z M6 4.79294V6H4.79294L6 4.79294Z M0.207151 0L0 0.207151V0H0.207151Z',
            stroke: '#C6CBD1',
            strokeWidth: 1,
          },
          width: 6,
          height: 6,
        },
        {
          id: idMissingDataWithAfibPattern,
          path: {
            d: 'M0.207151 6H0V4.79294L4.79294 0H6V0.207151L0.207151 6Z M6 4.79294V6H4.79294L6 4.79294Z M0.207151 0L0 0.207151V0H0.207151Z',
            stroke: '#C6CBD1',
            strokeWidth: 1,
            fill: '#FFAB0066',
          },
          width: 6,
          height: 6,
        },
        {
          id: idMissingDataWithUnsaveAfibPattern,
          path: {
            d: 'M0.207151 6H0V4.79294L4.79294 0H6V0.207151L0.207151 6Z M6 4.79294V6H4.79294L6 4.79294Z M0.207151 0L0 0.207151V0H0.207151Z M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#C6CBD1',
            fill: 'rgba(255, 171, 0, 0.4)',
            strokeWidth: 1,
          },
          width: 6,
          height: 6,
        },
        {
          id: idUnsaveArtifactPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#657E97',
            strokeWidth: 1,
            fill: 'rgba(130, 140, 153, 0.4)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idUnsaveAfibPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#EB6D17',
            strokeWidth: 1,
            fill: 'rgba(255, 171, 0, 0.4)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idUnsaveOtherPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#F23D42',
            strokeWidth: 1,
            fill: 'rgba(242, 61, 66, 0.4)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idSelectedUnsaveArtifactPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#000030',
            strokeWidth: 1,
            fill: 'rgba(130, 140, 153, 0.8)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idSelectedUnsaveAfibPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#F23D42',
            strokeWidth: 1,
            fill: 'rgba(255, 171, 0, 0.8)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idSelectedUnsaveOtherPattern,
          path: {
            d: 'M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#B80000',
            strokeWidth: 1,
            fill: 'rgba(242, 61, 66, 0.8)',
          },
          width: 4,
          height: 4,
        },
        {
          id: idSelectedMissingDataWithUnsaveAfibPattern,
          path: {
            d: 'M0.207151 6H0V4.79294L4.79294 0H6V0.207151L0.207151 6Z M6 4.79294V6H4.79294L6 4.79294Z M0.207151 0L0 0.207151V0H0.207151Z M4 1H3V2H4V1Z M2 3H1V4H2V3Z',
            stroke: '#C6CBD1',
            fill: 'rgba(255, 171, 0, 0.8)',
            strokeWidth: 1,
          },
          width: 6,
          height: 6,
        },
      ],
    },
    chart: {
      alignTicks: false,
      zoomType: 'x',
      selectionMarkerFill: 'rgba(0, 163, 173, 0.15)',
      animation: false,
      spacing: [0, 0, 0, 1],
      style: {
        fontFamily: 'Lato, Arial, Helvetica, sans-serif',
      },
      ignoreHiddenSeries: false,
      margin: isOldUI ? [30, 33, 50, 1] : [30, 33, 30, 1],
      events: {
        load: eventLoad,
        click: eventClick,
      },
    },
    series: [
      {
        type: 'area',
        zones: dataAreaChartZone,
        data: dataAreaChart,
      },
      {
        type: 'column',
        data: dataColumnChart,
      },
      {
        type: 'spline',
        data: dataHrsData,
        color: CHART_COLOR.CERULEAN_BLUE,
        zones: dataNegativeHrsZones,
        marker: {
          // symbol: 'url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNyIgaGVpZ2h0PSIxNyIgdmlld0JveD0iMCAwIDE3IDE3IiBmaWxsPSJub25lIj4KICA8Y2lyY2xlIG9wYWNpdHk9IjAuMjUiIGN4PSI4LjUiIGN5PSI4LjM2NjIxIiByPSI4IiBmaWxsPSIjMEI2OEU2Ii8+CiAgPGNpcmNsZSBjeD0iOC41IiBjeT0iOC4zNjYyMSIgcj0iMyIgZmlsbD0iIzA5NTRCQSIvPgo8L3N2Zz4=)',
          symbol: 'circle',
          enabled: !isOldUI,
          radius: 0.1,
          states: {
            select: {
              enabled: true,
              fillColor: CHART_COLOR.CERULEAN_BLUE,
              lineColor: 'rgba(9,84,186, 0.25)',
              lineWidth: 8,
              radius: 3,
            },
          },
        },
      },
    ],
    boost: {
      //* enable boost cause all series merge in 1 array, ui and logic break
      enabled: false,
      // useGPUTranslations: true,
      // seriesThreshold: 1,
      // usePreallocated: true,
    },
    xAxis: [
      {
        opposite: !isOldUI,
        tickInterval: 3600 * 1000,
        tickLength: 5,
        gridLineWidth: 0, // *: Hide x-axis grid,
        min: firstEpoch,
        max: firstEpoch + DAY_EPOCH,
        crosshair: {
          color: CHART_COLOR.TURQUOISE_BLUE,
        },
        labels: {
          align: 'left',
          format: '{value:%H:%M}',
          step: 2,
        },
        minRange: 4317000, // 1.2 hours
        // *: Display scrollbar while zooming
        scrollbar: {
          enabled: true,
          zIndex: 0,
          showFull: false,
          height: 10,
          trackBackgroundColor: '#F5F7FA',
          barBackgroundColor: '#DCE1E5',
          rifleColor: '#DCE1E5',
          barBorderRadius: 5,
          barBorderWidth: 0,
          buttonBorderWidth: 0,
          trackBorderWidth: 0,
          trackBorderRadius: 5,
          buttonBorderRadius: 5,
          buttonBackgroundColor: 'transparent',
          margin: isOldUI ? undefined : 23,
        },
        events: {
          afterSetExtremes: eventAfterSetExtremes,
        },
      },
    ],
    yAxis: [
      {
        title: {
          text: null as any,
        },
        opposite: true,
        min: minHr,
        max: maxHr,
        gridLineWidth: 0,
        lineWidth: 1,
        tickLength: 5,
        tickWidth: 1,
        showLastLabel: true,
        labels: {
          align: 'left',
          x: 12,
          y: 3,
        },
        startOnTick: false, // *: Setting to display min/max
        stopOnTick: false, // *: Setting to display min/max
        tickPositioner: () => {
          // *: To determine displayed ticks
          const positions = [minHr, Math.ceil((maxHr + minHr) / 2), maxHr];
          return positions;
        },
      },
    ],
    title: {
      text: null as any,
    },
    tooltip: {
      animation: false,
      hideDelay: 0,
      useHTML: true,
      shared: true,
      split: false,
      backgroundColor: null as any,
      borderWidth: 0,
      shadow: false,
      style: {
        padding: 0,
      },
      /**
       * Renders the body of the shared heart-rate chart tooltip. Takes the first non-empty tooltipText among the three hovered series points and wraps it in a div carrying the tooltip class.
       *
       * @returns An HTML string holding the tooltip content.
       */
      formatter() {
        const [firstPoint, secondPoint, thirdPoint] = (this as any).points;
        return `<div class="hr-chart-tooltip">${firstPoint.point.tooltipText || secondPoint.point.tooltipText || thirdPoint.point.tooltipText}</div>`;
      },
      /**
       * Places the heart-rate chart tooltip so it stays inside the plot area. Centres the label on the crosshair, clamping it to 130 pixels from the right edge when it would overflow there and to minus 8 when it would overflow left, and pins the vertical offset to 1.
       *
       * @param labelWidth - Rendered width of the tooltip label.
       * @param labelHeight - Rendered height of the tooltip label, unused by the clamping.
       * @param point - The hovered point, read for its plotX crosshair position.
       * @returns An object carrying the x and y pixel position for the tooltip.
       */
      positioner(labelWidth: number, labelHeight: number, point: any) {
        const { chartWidth } = (this as any).chart;
        let calculateX;
        if (point.plotX + labelWidth / 2 > chartWidth - 20) {
          // overflow right
          calculateX = chartWidth - 130;
        } else if (point.plotX - labelWidth / 2 < 0) {
          // overflow left
          calculateX = -8;
        } else {
          calculateX = point.plotX - labelWidth / 2; //* plotX is crosshair position
        }
        return { x: calculateX, y: 1 };
      },
    },
    plotOptions: {
      series: {
        // boostThreshold: 1440,
        turboThreshold: 1440,
        animation: false,
        shadow: false,
        dataLabels: { style: { textShadow: false } },
        zoneAxis: 'x',
        grouping: false,
        dataGrouping: {
          enabled: false,
        },
        states: {
          inactive: {
            enabled: false,
          },
          hover: {
            enabled: false,
          },
        },
        marker: {
          enabled: false,
        },
      },
      column: {
        pointRange: 30 * 1000,
        // pointRange: 1, //* important if null some column will disappear when zoom 100%
        pointWidth: 0.5,
        minPointLength: 62, // *: Make guides always have the same height of chart
        enableMouseTracking: false,
        clip: false,
      },
    },
    time: {
      timezoneOffset: -1 * timezoneOffset,
    },
    credits: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
  };
  // config isn't a structural match for Highcharts.Options (nullable title/tooltip text, formatter/positioner
  // `this` context, custom defs patterns) - cast at the boundary rather than restructure the object.
  return config as unknown as Highcharts.Options;
};

// if event into other event, it will be deleted
/**
 * Drops chart event bands that sit entirely inside another band. Clones the list, collects every entry strictly enclosed by some other entry's start and stop indices, and hands back the original untouched when nothing is enclosed.
 *
 * @param arr - The event bands, each with a startIndex and stopIndex.
 * @returns The band list with fully contained bands removed.
 */
const removeEventOverLap = (arr: any[]) => {
  const cloneArr = _.cloneDeep(arr);
  const listDelete: any[] = [];
  cloneArr.forEach((e) => {
    if (cloneArr.find((x) => x.startIndex < e.startIndex && e.stopIndex < x.stopIndex)) {
      listDelete.push(e);
    }
  });
  if (listDelete.length === 0) {
    return arr;
  }
  _.remove(cloneArr, (x) => listDelete.includes(x));
  return cloneArr;
};

/**
 * Builds hatched zone bands marking unsaved events on the daily heart-rate chart. For an event spanning more than one minute it emits a white stop at the start index and a pattern stop at the stop index, clamped into the 0 to 1439 minute range and labelled with the local hour and minute, while a single-minute event instead repaints the matching guide point.
 *
 * @param unSaveEvent - The unsaved events, each with a startIndex and stopIndex in minutes.
 * @param pointData - The day's minute points, indexed to resolve each event's epoch.
 * @param patternURL - The hatch pattern colour marking an unsaved event.
 * @param timezoneOffset - Offset applied when formatting each stop's time label.
 * @returns An array of zone stops for the unsaved events.
 */
const getZoneUnsaveEvent = (
  unSaveEvent: any[] = [],
  pointData: any[],
  patternURL: any,
  timezoneOffset: number,
) => {
  const eventsZones: any[] = [];
  unSaveEvent.forEach((item) => {
    if (item.stopIndex > item.startIndex) {
      if (!eventsZones.find((x) => x.value === pointData[item.startIndex]?.x)) {
        const index = item.startIndex < 0 ? 0 : item.startIndex;
        eventsZones.push({
          value: pointData[index]?.x,
          color: CHART_COLOR.WHITE,
          time: convertToMomentJs(pointData[index]?.x, timezoneOffset)?.format('HH:mm'),
        });
      }
      const index = item.stopIndex > 1439 ? 1439 : item.stopIndex;
      eventsZones.push({
        value: pointData[index]?.x,
        color: patternURL,
        time: convertToMomentJs(pointData[index]?.x, timezoneOffset)?.format('HH:mm'),
      });
    } else {
      const foundGuide = pointData.find((guide) => guide.index === item.startIndex);
      if (foundGuide) {
        foundGuide.color = patternURL;
      }
    }
  });
  return eventsZones;
};

const getZoneUnsaveAfibEvent = ({
  unsaveAfibEvents,
  afibEventAreaPointsData,
  missingPointsZone,
  idUnsaveAfibPatternUrl,
  timezoneOffset,
}: {
  unsaveAfibEvents: any[];
  afibEventAreaPointsData: any[];
  missingPointsZone: any[];
  idUnsaveAfibPatternUrl: any;
  timezoneOffset: number;
}) => {
  const unsaveAfibEventsZones: any[] = [];
  unsaveAfibEvents.forEach((item) => {
    if (item.stopIndex > item.startIndex) {
      // *: Range of unsave afib event
      if (
        !unsaveAfibEventsZones.find((x) => x.value === afibEventAreaPointsData[item.startIndex]?.x)
      ) {
        const index = item.startIndex < 0 ? 0 : item.startIndex;
        unsaveAfibEventsZones.push({
          value: afibEventAreaPointsData[index]?.x,
          color: CHART_COLOR.WHITE,
          time: convertToMomentJs(afibEventAreaPointsData[index]?.x, timezoneOffset).format(
            'HH:mm',
          ),
        });
      }

      const startMissingPointsInsideUnsaveAfibEvent = missingPointsZone.filter(
        (x) => x.value > item.startTimeEpoch && x.value < item.stopTimeEpoch,
      );
      if (startMissingPointsInsideUnsaveAfibEvent.length > 0) {
        startMissingPointsInsideUnsaveAfibEvent.forEach(
          (startMissingPointInsideUnsaveAfibEvent) => {
            unsaveAfibEventsZones.push({
              value: startMissingPointInsideUnsaveAfibEvent.value,
              color: idUnsaveAfibPatternUrl,
              time: convertToMomentJs(
                startMissingPointInsideUnsaveAfibEvent.value,
                timezoneOffset,
              ).format('HH:mm'),
            });
          },
        );
      }

      const index = item.stopIndex > 1439 ? 1439 : item.stopIndex;
      unsaveAfibEventsZones.push({
        value: afibEventAreaPointsData[index]?.x,
        color: idUnsaveAfibPatternUrl,
        time: convertToMomentJs(afibEventAreaPointsData[index]?.x, timezoneOffset).format('HH:mm'),
      });
    } else {
      // *: Single point of unsave afib event
      const foundGuide = afibEventAreaPointsData.find((guide) => guide.index === item.startIndex);
      if (foundGuide) {
        foundGuide.color = idUnsaveAfibPatternUrl;
      }
    }
  });
  return unsaveAfibEventsZones;
};

export const generateDataChartFromRawData = ({
  hrs,
  missingPoints,
  artifactEventGuides,
  artifactEventAreaPoints,
  afibEventGuides,
  afibEventAreaPoints,
  otherEventGuides,
  otherEventAreaPoints,
  unsaveArtifactEvents,
  unsaveAfibEvents,
  unsaveOtherEvents,
  timezoneOffset = 0,
  minHr,
  eventClickSplineChart,
  idHtmlChart,
  eventIndicators,
}: {
  hrs: any[];
  missingPoints: any[];
  artifactEventGuides: any[];
  artifactEventAreaPoints: any[];
  afibEventGuides: any[];
  afibEventAreaPoints: any[];
  otherEventGuides: any[];
  otherEventAreaPoints: any[];
  unsaveArtifactEvents: any[];
  unsaveAfibEvents: any[];
  unsaveOtherEvents: any[];
  timezoneOffset?: number;
  minHr: number;
  eventClickSplineChart: any;
  idHtmlChart: any;
  eventIndicators: any[];
}) => {
  const idMissingDataPatternUrl = `url(#missing-data-custom-pattern-${idHtmlChart})`;
  const idMissingDataWithAfibPatternUrl = `url(#missing-data-with-afib-custom-pattern-${idHtmlChart})`;
  const idMissingDataWithUnsaveAfibPatternUrl = `url(#missing-data-with-unsave-afib-custom-pattern-${idHtmlChart})`;
  const idUnsaveArtifactPatternUrl = `url(#unsave-artifact-event-custom-pattern-${idHtmlChart})`;
  const idUnsaveAfibPatternUrl = `url(#unsave-afib-event-custom-pattern-${idHtmlChart})`;
  const idUnsaveOtherPatternUrl = `url(#unsave-other-event-custom-pattern-${idHtmlChart})`;

  // *: Included unsaved
  const afibEventIndicators = eventIndicators.filter((x) => x.type === TYPE_RHYTHM_EVENT_ENUM.AFIB);

  const hrsData = hrs.map((d, index) => {
    const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
    const avgHr = d[1];
    return {
      index,
      x: d[0],
      y: avgHr,
      id: d[0],
      maxHr: d[2],
      minHr: d[3],
      avgHr,
      tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
      events: {
        click: eventClickSplineChart,
      },
    };
  });

  const missingPointsData: any[] = [];
  const missingPointsZone: any[] = [];
  missingPoints.forEach((d, index) => {
    const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
    missingPointsData.push({
      x: d[0],
      y: d[1],
      tooltipText: `${time} - No data`,
    });

    if (!_.isNil(hrsData[index].y)) {
      if (_.isNil(hrsData[index - 1]?.y)) {
        // *: This point is position that end missing point zone, start has data zone
        const hasUnsaveAfib = !!_.find(
          unsaveAfibEvents,
          (x) =>
            x.startIndex !== x.stopIndex &&
            ((_.last(missingPointsZone) && _.last(missingPointsZone).index >= x.startIndex) ||
              (x.startIndex < 0 && !_.last(missingPointsZone))) &&
            index <= x.stopIndex,
        );
        const hasAfib = !!_.find(
          afibEventIndicators,
          (x) =>
            x.startIndex !== x.stopIndex &&
            ((_.last(missingPointsZone) && _.last(missingPointsZone).index >= x.startIndex) ||
              (x.startIndex < 0 && !_.last(missingPointsZone))) &&
            index <= x.stopIndex,
        );
        missingPointsZone.push({
          value: missingPointsData[index].x,
          color: hasUnsaveAfib
            ? idMissingDataWithUnsaveAfibPatternUrl
            : hasAfib
              ? idMissingDataWithAfibPatternUrl
              : idMissingDataPatternUrl,
          time: convertToMomentJs(missingPointsData[index].x, timezoneOffset).format('HH:mm'),
          index,
        });
      }
      if (_.isNil(hrsData[index + 1]?.y)) {
        // *: This point is position that end has data zone, start missing point zone
        missingPointsZone.push({
          value: missingPointsData[index].x,
          color: CHART_COLOR.WHITE,
          time: convertToMomentJs(missingPointsData[index].x, timezoneOffset).format('HH:mm'),
          index,
        });
      }
    } else if (_.isNil(hrsData[index + 1])) {
      // *: This point is the last point of chart and end missing point zone
      const hasUnsaveAfib = !!_.find(
        unsaveAfibEvents,
        (x) =>
          x.startIndex !== x.stopIndex &&
          ((_.last(missingPointsZone) && _.last(missingPointsZone).index >= x.startIndex) ||
            (x.startIndex < 0 && !_.last(missingPointsZone))) &&
          index <= x.stopIndex,
      );
      const hasAfib = !!_.find(
        afibEventIndicators,
        (x) =>
          x.startIndex !== x.stopIndex &&
          ((_.last(missingPointsZone) && _.last(missingPointsZone).index >= x.startIndex) ||
            (x.startIndex < 0 && !_.last(missingPointsZone))) &&
          index <= x.stopIndex,
      );
      missingPointsZone.push({
        value: missingPointsData[index].x,
        color: hasUnsaveAfib
          ? idMissingDataWithUnsaveAfibPatternUrl
          : hasAfib
            ? idMissingDataWithAfibPatternUrl
            : idMissingDataPatternUrl,
        time: convertToMomentJs(missingPointsData[index].x, timezoneOffset).format('HH:mm'),
        index,
      });
    }
  });
  const artifactEventGuidesData = artifactEventGuides.map((d, index) => ({
    x: d[0],
    y: d[1],
    index,
    color: CHART_COLOR.STEEL_GRAY_LIGHT,
    time: convertToMomentJs(d[0], timezoneOffset).format('HH:mm'),
  }));
  const artifactEventAreaPointsData = artifactEventAreaPoints.map((d) => {
    const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
    const avgHr = d[2];
    return {
      x: d[0],
      y: d[1],
      avgHr,
      tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
    };
  });
  const afibEventGuidesData = afibEventGuides.map((d, index) => ({
    x: d[0],
    y: d[1],
    index,
    color: CHART_COLOR.AMBER_YELLOW_LIGHT,
    time: convertToMomentJs(d[0], timezoneOffset).format('HH:mm'),
  }));
  const afibEventAreaPointsData = afibEventAreaPoints.map((d) => {
    const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
    const avgHr = d[2];
    return {
      x: d[0],
      y: d[1],
      avgHr,
      tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
    };
  });
  const otherEventGuidesData = otherEventGuides.map((d, index) => ({
    x: d[0],
    y: d[1],
    color: CHART_COLOR.CRIMSON_RED_LIGHT,
    index,
    time: convertToMomentJs(d[0], timezoneOffset).format('HH:mm'),
  }));
  const otherEventAreaPointsData = otherEventAreaPoints.map((d) => {
    const time = convertToMomentJs(d[0], timezoneOffset).format('HH:mm');
    const avgHr = d[2];
    return {
      x: d[0],
      y: d[1],
      avgHr,
      tooltipText: isValidHr(avgHr) ? `${time} - ${avgHr} bpm` : `${time} - NA`,
    };
  });

  // For ArtifactEvent, remove event artifact overlap together
  const unsaveArtifactEventsZones = getZoneUnsaveEvent(
    removeEventOverLap(unsaveArtifactEvents),
    artifactEventAreaPointsData,
    idUnsaveArtifactPatternUrl,
    timezoneOffset,
  );
  const unsaveAfibEventsZones = getZoneUnsaveAfibEvent({
    unsaveAfibEvents,
    afibEventAreaPointsData,
    missingPointsZone,
    idUnsaveAfibPatternUrl,
    timezoneOffset,
  });
  const unsaveOtherEventsZones = getZoneUnsaveEvent(
    unsaveOtherEvents,
    otherEventAreaPointsData,
    idUnsaveOtherPatternUrl,
    timezoneOffset,
  );

  const negativeHrsZones: any[] = [];
  hrsData.forEach((item, index) => {
    if (index === hrsData.length - 1) {
      // *: Draw until last point
      if (item.avgHr === -1 && hrsData[index - 1] && hrsData[index - 1]?.avgHr === -1) {
        negativeHrsZones.push({
          value: hrsData[index]?.x,
          dashStyle: 'dash',
        });
      }
    } else if (item.avgHr === -1) {
      if (
        hrsData[index - 1] &&
        (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
        hrsData[index + 1] &&
        (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
      ) {
        negativeHrsZones.push(
          { value: hrsData[index - 1]?.x },
          {
            value: hrsData[index + 1]?.x,
            dashStyle: 'dash',
          },
        );
      } else if (
        hrsData[index - 1] &&
        (_.isNil(hrsData[index - 1]?.avgHr) || hrsData[index - 1]?.avgHr > -1) &&
        hrsData[index + 1] &&
        hrsData[index + 1]?.avgHr === -1
      ) {
        negativeHrsZones.push({ value: hrsData[index - 1]?.x });
      } else if (
        hrsData[index - 1] &&
        hrsData[index - 1]?.avgHr === -1 &&
        hrsData[index + 1] &&
        (_.isNil(hrsData[index + 1]?.avgHr) || hrsData[index + 1]?.avgHr > -1)
      ) {
        negativeHrsZones.push({
          value: hrsData[index + 1]?.x,
          dashStyle: 'dash',
        });
      }
    }
  });

  // *: Re-assign y for dash HR point to draw dash line
  negativeHrsZones.forEach((zone, index) => {
    if (zone.dashStyle && index === 0) {
      const dashHrs = hrsData.filter((item) => item.x <= zone.value);
      dashHrs.forEach((dashHr) => {
        const foundHr = hrsData.find((item) => item.x === dashHr.x);
        if (foundHr) {
          if (!_.isNil(foundHr.y)) {
            foundHr.y = minHr;
          }
        }
      });
    } else if (
      zone.dashStyle &&
      negativeHrsZones[index - 1] &&
      !negativeHrsZones[index - 1].dashStyle
    ) {
      const dashHrs = hrsData.filter(
        (item) => item.x >= negativeHrsZones[index - 1].value && item.x <= zone.value,
      );
      dashHrs.forEach((dashHr) => {
        const foundHr = hrsData.find((item) => item.x === dashHr.x);
        if (foundHr) {
          if (!_.isNil(foundHr.y)) {
            foundHr.y = minHr;
          }
        }
      });
    }
  });

  return {
    hrsData,
    missingPointsData,
    artifactEventGuidesData,
    artifactEventAreaPointsData,
    afibEventGuidesData,
    afibEventAreaPointsData,
    otherEventGuidesData,
    otherEventAreaPointsData,
    missingPointsZone,
    unsaveArtifactEventsZones,
    unsaveAfibEventsZones,
    unsaveOtherEventsZones,
    negativeHrsZones,
  };
};

/**
 * Picks the preset zoom step the heart-rate chart should move to for a zoom-in or zoom-out. When the current scale is itself a preset it steps one position in the requested direction, clamped at both ends, and otherwise finds the first preset above the current scale and returns that index when zooming in or the one below it when zooming out.
 *
 * @param zoomScalee - The current zoom percentage.
 * @param isZoomIn - Whether the step moves towards a closer zoom.
 * @returns The index into the preset zoom scale list to apply.
 */
export const getZoomScaleIndexToSet = (zoomScalee: number, isZoomIn = false) => {
  const foundIndexInOptions = _.findIndex(ZOOM_SCALE_DEFAULT, (x) => zoomScalee === x);
  if (foundIndexInOptions !== -1) {
    if (isZoomIn) {
      return foundIndexInOptions + 1 > ZOOM_SCALE_DEFAULT.length - 1
        ? foundIndexInOptions
        : foundIndexInOptions + 1;
    }
    return foundIndexInOptions - 1 < 0 ? foundIndexInOptions : foundIndexInOptions - 1;
  }
  const foundIndexNotInOptions = _.findIndex(ZOOM_SCALE_DEFAULT, (x) => zoomScalee < x);
  return isZoomIn ? foundIndexNotInOptions : foundIndexNotInOptions - 1;
};

/**
 * Converts a visible heart-rate chart window into a zoom percentage relative to a full day. Divides one day by the window span, fixes the ratio to two decimals and scales it by 100, relying on the fixed string coercing back to a number.
 *
 * @param min - Epoch at the left edge of the visible window.
 * @param max - Epoch at the right edge of the visible window.
 * @returns The zoom percentage, rounded to a whole number.
 */
export const getZoomScale = (min: number, max: number) => {
  const zoomLevel = Number(1 / ((max - min) / DAY_EPOCH)).toFixed(2);
  // pre-existing: toFixed(2) returns a string, multiplied here as if it were still a number
  return Math.round((zoomLevel as any) * 100);
};

// Extracted from the byte-identical handleSetRangeWhenZooming closures in
// entities/heart-rate/model/useChartHR.ts and widgets/strips-management/ui/hrChart/useHrChart.ts.
// Pure math only — the caller still owns reading the current chart's xAxis extremes and calling
// `.setExtremes(...)` on its own chartRef.
export const calculateZoomRangeOnSelect = ({
  min,
  max,
  dataMin,
  dataMax,
  category,
}: {
  min: number;
  max: number;
  dataMin: number;
  dataMax: number;
  category: number;
}): { calculateMin: number; calculateMax: number } | undefined => {
  const zoomScalee = getZoomScale(min, max);
  //* isZooming
  if (zoomScalee <= 100) {
    return undefined;
  }
  const isSelectPointInZoomView = category > min && category < max;
  if (isSelectPointInZoomView) {
    return undefined;
  }
  const currentZoomRange = max - min;
  const tempMin = category - currentZoomRange / 2;
  const tempMax = category + currentZoomRange / 2;
  const outOfRangeMax = tempMax - dataMax;
  const outOfRangeMin = tempMin - dataMin;
  const isOutOfRangeMax = outOfRangeMax > 0;
  const isOutOfRangeMin = outOfRangeMin < 0;
  let calculateMin;
  let calculateMax;
  if (isOutOfRangeMax) {
    calculateMin = tempMin - Math.abs(outOfRangeMax);
    calculateMax = dataMax;
  } else if (isOutOfRangeMin) {
    calculateMin = dataMin;
    calculateMax = tempMax + Math.abs(outOfRangeMin);
  } else {
    calculateMin = tempMin;
    calculateMax = tempMax;
  }
  return { calculateMin, calculateMax };
};

// Consolidated from 3 byte-identical widget copies (widgets/ecg-events/ui/useHrChart.ts,
// widgets/ecg-events/ui/ecgBookmark/useBookmarkHrChart.ts, widgets/beats/ui/afibEvents/useAfibHrChart.ts)
// — each hook is still responsible for its own store-bound setters, passed in explicitly so
// behavior per widget is unchanged.
export const formatHrData = ({
  holterEventsByDate = [] as any[],
  needLoading,
  ecgDataMap,
  timezoneOffset,
  selectedDateValue,
  setDataChart,
  setIsEmptyData,
  setIsLoadingHrChart,
}: {
  holterEventsByDate?: any[];
  needLoading?: boolean;
  ecgDataMap: any;
  timezoneOffset: any;
  selectedDateValue: any;
  setDataChart: (data: any) => void;
  setIsEmptyData: (value: boolean) => void;
  setIsLoadingHrChart: (value: boolean) => void;
}) => {
  try {
    let hasNoData;
    const selectedDateMoment = convertToMomentJs(selectedDateValue, timezoneOffset);
    const { data, channels, samplingFrequency } = ecgDataMap;
    if (data?.length && selectedDateMoment && !data.find((x: any) => !x.dataUrl)) {
      const startDayMoment = selectedDateMoment.clone().startOf('days');
      const stopDayMoment = selectedDateMoment.clone().endOf('days');

      // *: Filter HR data in date
      const currentDateData = filterHourlySummaryRanges({
        summaries: ecgDataMap,
        start: startDayMoment,
        stop: stopDayMoment,
        channels,
        samplingFrequency,
        willAddDataRanges: false,
      } as any);

      const isValidCurrentDateData = validateEcgDataMap(currentDateData);
      if (isValidCurrentDateData) {
        const filteredAvgHrArray: any[] = [];
        const filteredMaxHrArray: any[] = [];
        const filteredMinHrArray: any[] = [];
        currentDateData.forEach((item: any) => {
          filteredAvgHrArray.push(item.avgHrs);
          filteredMaxHrArray.push(item.maxHrs);
          filteredMinHrArray.push(item.minHrs);
        });
        const [avgHrArray, maxHrArray, minHrArray]: any[] = createMinutelyHrChartData({
          hourlySummaries: currentDateData,
          start: startDayMoment,
          stop: stopDayMoment,
          minutelyHrs: filteredAvgHrArray,
          maxMinutelyHrs: filteredMaxHrArray,
          minMinutelyHrs: filteredMinHrArray,
          isEditReportPage: true,
        }) as any;
        if (_.find(avgHrArray, (x) => !_.isNil(x))) {
          // *: All data are null
          let minHr = 0;
          let maxHr = 150;
          const clonedAvgHrArray = [...avgHrArray];
          _.remove(clonedAvgHrArray, (x) => x < 0);
          if (clonedAvgHrArray.length > 0) {
            const minHrTmp = Math.floor(_.min(clonedAvgHrArray) / 10) * 10;
            const maxHrTmp = Math.ceil(_.max(clonedAvgHrArray) / 10) * 10;
            const { min, max } = getMinMaxChart(minHrTmp, maxHrTmp);
            minHr = min || 0;
            maxHr = max || 150;
          }

          const firstEpoch = startDayMoment.valueOf();
          const eventIndicators = holterEventsByDate.map((holterEvent) => {
            const roundStartMoment = moment(holterEvent.start).startOf('minutes');
            const roundStopMoment = moment(holterEvent.stop).startOf('minutes');
            return {
              ...holterEvent,
              startIndex: (roundStartMoment.valueOf() - firstEpoch) / 1000 / 60,
              stopIndex: (roundStopMoment.valueOf() - firstEpoch) / 1000 / 60,
            };
          });

          const {
            hrs,
            guides,
            missingPoints,
            artifactEventGuides,
            artifactEventAreaPoints,
            afibEventGuides,
            afibEventAreaPoints,
            otherEventGuides,
            otherEventAreaPoints,
            unsaveArtifactEvents,
            unsaveAfibEvents,
            unsaveOtherEvents,
          } = createChartData({
            hrArray: avgHrArray,
            eventIndicators,
            firstEpoch,
            maxHr, //* use max hr to fill null data and draw zone
            maxHrArray,
            minHrArray,
            isEditReportPage: true,
          });

          setDataChart({
            hrs,
            guides,
            missingPoints,
            artifactEventGuides,
            artifactEventAreaPoints,
            afibEventGuides,
            afibEventAreaPoints,
            otherEventGuides,
            otherEventAreaPoints,
            unsaveArtifactEvents,
            unsaveAfibEvents,
            unsaveOtherEvents,
            minHr,
            maxHr,
            eventIndicators,
            firstEpoch,
            isZoomAfterDrawChart: !needLoading,
          });
        } else {
          hasNoData = true;
        }
      } else {
        hasNoData = true;
      }
    } else {
      hasNoData = true;
    }
    if (hasNoData) {
      setIsEmptyData(true);
      setIsLoadingHrChart(false);
    }
  } catch (error) {
    logError('Failed to fetch HR data: ', error);
    toastrError(error.message, 'Error');
    setIsEmptyData(true);
    setIsLoadingHrChart(false);
  }
};
